import type { Difficulty } from '../songpack/index.js';
import { NEUTRAL_TUNING } from './ChartTuning.js';
import { toWire } from './ChartDirection.js';
import type { AutoParameters, ChartTuning, EventSources } from './ChartTuning.js';
import { SEMANTIC_ROLES } from './ChartDirection.js';
import type {
  DetectedSection,
  SectionAnalysis,
  SectionSemantics,
  SemanticAnalysis,
  SemanticRole,
} from './ChartDirection.js';
import {
  ChartGenerationError,
  type ChartEngineHealth,
  type ChartGenerationClient,
  type ChartGenerationProgress,
  type ChartGenerationRequest,
  type ChartGenerationSummary,
  type GenerateOptions,
  type GeneratedSongPack,
} from './ChartGenerationClient.js';

/**
 * `ChartGenerationClient` over the local HTTP bridge.
 *
 * This is the only file in the runtime that knows the engine has a URL. Nothing
 * above it constructs a request, and nothing above it fetches — which is what
 * makes an Electron or Tauri build a matter of adding a sibling to this file.
 *
 * Three round trips, in this order:
 *
 *   POST /generate           blocks for the whole analysis (tens of seconds)
 *   GET  /progress/{jobId}   polled meanwhile, on a second connection
 *   GET  /songpack/{token}   collects the archive the engine built
 *
 * The split matters. A single POST cannot stream progress back while its own
 * request body is still being consumed, so the job id is generated *here* and
 * sent with the request, letting the browser ask about a job that has not
 * finished being described yet.
 */

const DEFAULT_BASE_URL = 'http://127.0.0.1:8787';
const DEFAULT_POLL_INTERVAL_MS = 220;

export interface HttpChartGenerationClientOptions {
  /** Origin the bridge listens on. Trailing slashes are tolerated. */
  readonly baseUrl?: string;
  readonly pollIntervalMs?: number;
  /** Injectable for tests; defaults to the global `fetch`. */
  readonly fetch?: typeof globalThis.fetch;
  /** Injectable for tests; defaults to `crypto.randomUUID`. */
  readonly newJobId?: () => string;
}

export class HttpChartGenerationClient implements ChartGenerationClient {
  readonly baseUrl: string;

  private readonly transport: typeof globalThis.fetch;
  private readonly pollIntervalMs: number;
  private readonly newJobId: () => string;

  constructor(options: HttpChartGenerationClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, '');
    // Bound to `globalThis`: an unbound `fetch` throws "Illegal invocation".
    this.transport = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    this.newJobId = options.newJobId ?? (() => crypto.randomUUID());
  }

  async health(): Promise<ChartEngineHealth> {
    const response = await this.send('/health', { method: 'GET' });
    const body = await readJson(response);
    return {
      ok: readBoolean(body, 'ok'),
      engine: readString(body, 'engine'),
      bridgeVersion: readString(body, 'bridgeVersion'),
      pythonVersion: readString(body, 'pythonVersion'),
      configPath: readString(body, 'configPath'),
      maxUploadBytes: readNumber(body, 'maxUploadBytes'),
      acceptedAudio: readStringArray(body, 'acceptedAudio'),
      difficulties: readStringArray(body, 'difficulties'),
    };
  }

  async generate(
    request: ChartGenerationRequest,
    options: GenerateOptions = {},
  ): Promise<GeneratedSongPack> {
    const jobId = this.newJobId();
    const form = new FormData();
    form.set('packId', request.packId);
    form.set('title', request.title);
    form.set('artist', request.artist);
    form.set('jobId', jobId);
    // Sent only when the player actually corrected something. An engine
    // that predates corrections ignores unknown form fields, and one that
    // knows them treats their absence as AUTO, so both directions of the
    // version skew land on the automatic chart.
    if (request.mode === 'CUSTOM' && request.tuning !== undefined) {
      form.set('mode', 'CUSTOM');
      form.set('density', String(request.tuning.density));
      form.set('minimumIntervalScale', String(request.tuning.minimumIntervalScale));
      form.set('onsetSensitivity', String(request.tuning.onsetSensitivity));
      form.set('syncOffsetMs', String(request.tuning.syncOffsetMs));
      form.set('movement', String(request.tuning.movement));
    }
    if (request.direction !== undefined) {
      const wire = toWire(request.direction);
      // Nothing is sent when nothing was directed, so an engine that has
      // never heard of direction and one that has both produce the same
      // automatic chart.
      if (wire !== null) form.set('direction', JSON.stringify(wire));
    }
    if (request.autoDirect === true) form.set('directedAuto', '1');
    // Only ever sent to turn it off: an engine that predates the field treats
    // its absence as the behaviour it already had, and so does this one.
    if (request.semantic === false) form.set('semantic', '0');
    if (request.regenerateSection !== undefined) {
      const baseline =
        request.baselineDirection === undefined ? null : toWire(request.baselineDirection);
      if (baseline !== null) form.set('baselineDirection', JSON.stringify(baseline));
      if (request.resolvedDirections !== undefined) {
        form.set('resolvedDirections', JSON.stringify(request.resolvedDirections));
      }
      // The charts themselves, so the engine copies what it keeps.
      if (request.baselineCharts !== undefined) {
        form.set('baselineCharts', JSON.stringify(request.baselineCharts));
      }
      if (baseline !== null || request.baselineCharts !== undefined) {
        form.set('regenerateSection', String(request.regenerateSection));
      }
    }
    form.set(
      'audio',
      new Blob([request.audio.bytes], { type: request.audio.mimeType }),
      request.audio.fileName,
    );
    if (request.cover !== undefined) {
      form.set(
        'cover',
        new Blob([request.cover.bytes], { type: request.cover.mimeType }),
        request.cover.fileName,
      );
    }

    const stopPolling = this.pollProgress(jobId, options.onProgress);
    let body: Record<string, unknown>;
    try {
      const response = await this.send('/generate', {
        method: 'POST',
        body: form,
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      });
      body = await readJson(response);
    } catch (cause) {
      // Tell the engine to stop as well. Best effort: the job may already be
      // finished, and a failed cancel must not replace the real error.
      if (isAbort(cause, options.signal)) void this.requestCancel(jobId);
      throw cause;
    } finally {
      stopPolling();
    }

    const summary = readSummary(body);
    const token = readString(readRecord(body, 'songPack'), 'token');
    // Built here rather than taken from the response. The engine offers a `url`,
    // but following a URL a server chose is a habit worth not having, and this
    // also keeps the host consistent — 127.0.0.1 and localhost are different
    // origins, and only the one we were configured with is on its CORS list.
    const archive = await this.collect(token, options.signal);
    // Releases the engine's copy now that this one is safely in hand.
    void this.release(token);

    options.onProgress?.({ stage: 'Complete', message: 'Complete', fraction: 1 });
    return { summary: { ...summary, archiveBytes: archive.byteLength }, archive };
  }

  private async collect(token: string, signal: AbortSignal | undefined): Promise<ArrayBuffer> {
    const response = await this.send(`/songpack/${encodeURIComponent(token)}`, {
      method: 'GET',
      ...(signal === undefined ? {} : { signal }),
    });
    return response.arrayBuffer();
  }

  private async release(token: string): Promise<void> {
    try {
      await this.transport(`${this.baseUrl}/songpack/${encodeURIComponent(token)}`, {
        method: 'DELETE',
      });
    } catch {
      // The engine expires it on its own; failing to say so early is harmless.
    }
  }

  private async requestCancel(jobId: string): Promise<void> {
    try {
      await this.transport(`${this.baseUrl}/cancel/${encodeURIComponent(jobId)}`, {
        method: 'POST',
      });
    } catch {
      // Nothing to do: the caller is already unwinding with its own error.
    }
  }

  /**
   * Polls progress until the returned function is called.
   *
   * Deliberately silent about its own failures. Progress is a comfort, not a
   * result: if the poll 404s or the engine is mid-restart, the generation it is
   * describing is still running and its outcome still arrives on the POST.
   */
  private pollProgress(
    jobId: string,
    onProgress: ((progress: ChartGenerationProgress) => void) | undefined,
  ): () => void {
    if (onProgress === undefined) return () => undefined;
    let stopped = false;
    let last = '';
    const tick = async (): Promise<void> => {
      if (stopped) return;
      try {
        const response = await this.transport(
          `${this.baseUrl}/progress/${encodeURIComponent(jobId)}`,
          { method: 'GET' },
        );
        if (response.ok) {
          const value = (await response.json()) as Record<string, unknown>;
          const stage = typeof value['stage'] === 'string' ? value['stage'] : '';
          const message = typeof value['message'] === 'string' ? value['message'] : stage;
          const fraction = typeof value['fraction'] === 'number' ? value['fraction'] : 0;
          const key = `${stage}|${message}|${fraction}`;
          // Repeats are suppressed so a screen that logs each step shows a list
          // of stages rather than the same line two hundred times.
          if (!stopped && key !== last) {
            last = key;
            onProgress({ stage, message, fraction });
          }
        }
      } catch {
        // See above.
      }
      if (!stopped) timer = setTimeout(() => void tick(), this.pollIntervalMs);
    };
    let timer = setTimeout(() => void tick(), this.pollIntervalMs);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }

  /** One request, with every failure mode turned into a typed error. */
  private async send(path: string, init: RequestInit): Promise<Response> {
    let response: Response;
    try {
      response = await this.transport(`${this.baseUrl}${path}`, init);
    } catch (cause) {
      if (isAbort(cause, init.signal ?? undefined)) {
        throw new ChartGenerationError('cancelled', 'Generation was cancelled.', { cause });
      }
      throw new ChartGenerationError(
        'unreachable',
        `The chart engine did not answer at ${this.baseUrl}. Start it with ` +
          '"python -m mandalizm_chart_engine.web_bridge".',
        { cause },
      );
    }
    if (response.ok) return response;
    const detail = await readDetail(response);
    if (response.status === 409) {
      throw new ChartGenerationError('cancelled', detail, { status: 409 });
    }
    throw new ChartGenerationError(
      response.status >= 500 ? 'engine-failed' : 'rejected',
      detail,
      { status: response.status },
    );
  }
}

// ---------------------------------------------------------------------------

function isAbort(cause: unknown, signal: AbortSignal | undefined): boolean {
  if (signal?.aborted === true) return true;
  return cause instanceof DOMException && cause.name === 'AbortError';
}

async function readDetail(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === 'string' && body.detail.length > 0) return body.detail;
  } catch {
    // Not JSON. Fall through to the status line.
  }
  return `${response.status} ${response.statusText}`;
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  let value: unknown;
  try {
    value = await response.json();
  } catch (cause) {
    throw new ChartGenerationError(
      'malformed-response',
      'The chart engine returned a body that is not JSON.',
      { cause },
    );
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ChartGenerationError('malformed-response', 'Expected a JSON object.');
  }
  return value as Record<string, unknown>;
}

function readSummary(
  source: Record<string, unknown>,
): Omit<ChartGenerationSummary, 'archiveBytes'> {
  const events = readRecord(source, 'events');
  return {
    packId: readString(source, 'packId'),
    songId: readString(source, 'songId'),
    displayName: readString(source, 'displayName'),
    author: typeof source['author'] === 'string' ? source['author'] : '',
    detectedBpm: readNumber(source, 'detectedBpm'),
    firstBeatOffsetSeconds: readNumber(source, 'firstBeatOffsetSeconds'),
    durationSeconds: readNumber(source, 'durationSeconds'),
    sampleRate: readNumber(source, 'sampleRate'),
    beatCount: readNumber(source, 'beatCount'),
    onsetCount: readNumber(source, 'onsetCount'),
    beatTimes: Array.isArray(source['beatTimes'])
      ? (source['beatTimes'] as unknown[]).filter(
          (value): value is number => typeof value === 'number',
        )
      : [],
    events: {
      easy: readNumber(events, 'easy'),
      normal: readNumber(events, 'normal'),
      hard: readNumber(events, 'hard'),
    } satisfies Record<Difficulty, number>,
    coverIncluded: source['coverIncluded'] === true,
    elapsedSeconds: typeof source['elapsedSeconds'] === 'number' ? source['elapsedSeconds'] : 0,
    generatorMode: source['generatorMode'] === 'CUSTOM' ? 'CUSTOM' : 'AUTO',
    generatorVersion:
      typeof source['generatorVersion'] === 'string' ? source['generatorVersion'] : '1.0.0',
    seed: typeof source['seed'] === 'number' ? source['seed'] : 0,
    // Null rather than a fabricated default when the engine did not send
    // them: an older bridge knows none of this, and inventing plausible
    // numbers would put values on the screen that no engine ever chose.
    autoParameters: readAutoParameters(source['autoParameters']),
    appliedParameters: readTuning(source['appliedParameters']),
    eventSources: readEventSources(source['eventSources']),
    eventSourcesScope: source['eventSourcesScope'] === 'section' ? 'section' : 'chart',
    directed: source['directed'] === true,
    geometryUsage: readEventSources(source['geometryUsage']),
    patternUsage: readEventSources(source['patternUsage']),
    geometrySequence: readSequences(source['geometrySequence']),
    sectionAnalysis: readSectionAnalysis(source['sectionAnalysis']),
    semanticAnalysis: readSemanticAnalysis(source['semanticAnalysis']),
    semanticDirected: source['semanticDirected'] === true,
    analysisVersions: readVersions(source['analysisVersions']),
    sectionDirection: Array.isArray(source['sectionDirection'])
      ? (source['sectionDirection'] as Record<string, unknown>[])
      : [],
    autoDirected: source['autoDirected'] === true,
    appliedDirection:
      source['appliedDirection'] !== null && typeof source['appliedDirection'] === 'object'
        ? (source['appliedDirection'] as Record<string, unknown>)
        : null,
    resolvedDirections: readDirections(source['resolvedDirections']),
    regeneratedSection:
      typeof source['regeneratedSection'] === 'number' ? source['regeneratedSection'] : null,
  };
}

function readAutoParameters(raw: unknown): AutoParameters | null {
  if (raw === null || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const measured = source['measured'];
  const defaults = source['defaults'];
  if (typeof measured !== 'object' || measured === null) return null;
  if (typeof defaults !== 'object' || defaults === null) return null;
  return {
    seed: typeof source['seed'] === 'number' ? source['seed'] : 0,
    measured: measured as AutoParameters['measured'],
    defaults: defaults as AutoParameters['defaults'],
  };
}

function readTuning(raw: unknown): ChartTuning | null {
  if (raw === null || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const number = (key: keyof ChartTuning): number =>
    typeof source[key] === 'number' ? (source[key] as number) : NEUTRAL_TUNING[key];
  return {
    density: number('density'),
    minimumIntervalScale: number('minimumIntervalScale'),
    onsetSensitivity: number('onsetSensitivity'),
    syncOffsetMs: number('syncOffsetMs'),
    movement: number('movement'),
  };
}

function readSectionAnalysis(raw: unknown): SectionAnalysis | null {
  if (raw === null || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  if (!Array.isArray(source['sections'])) return null;
  return {
    sections: source['sections'] as DetectedSection[],
    boundaries: Array.isArray(source['boundaries'])
      ? (source['boundaries'] as number[]).filter((value) => typeof value === 'number')
      : [],
  };
}

function readSequences(raw: unknown): Readonly<Record<string, readonly string[]>> | null {
  if (raw === null || typeof raw !== 'object') return null;
  const out: Record<string, string[]> = {};
  for (const [difficulty, names] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(names)) continue;
    out[difficulty] = names.filter((name): name is string => typeof name === 'string');
  }
  return out;
}

function readEventSources(raw: unknown): EventSources | null {
  if (raw === null || typeof raw !== 'object') return null;
  const out: Record<string, Record<string, number>> = {};
  for (const [difficulty, counts] of Object.entries(raw as Record<string, unknown>)) {
    if (counts === null || typeof counts !== 'object') continue;
    const layers: Record<string, number> = {};
    for (const [layer, value] of Object.entries(counts as Record<string, unknown>)) {
      if (typeof value === 'number') layers[layer] = value;
    }
    out[difficulty] = layers;
  }
  return out;
}

/**
 * The engine's per-difficulty directions, kept as they arrived.
 *
 * Deliberately not parsed into the browser's own `ChartDirection`: these exist
 * to be handed straight back when one section is regenerated, and a document
 * that has been through a shape the browser understands is a document that can
 * quietly lose whatever the browser does not.
 */
/**
 * The inferred reading, or null when the engine did not produce one.
 *
 * An older bridge sends nothing here and gets null, which the screen renders as
 * "no reading" rather than as a song with no structure — the difference between
 * not knowing and knowing there is nothing.
 */
function readSemanticAnalysis(raw: unknown): SemanticAnalysis | null {
  if (raw === null || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const rows = Array.isArray(source['sections']) ? source['sections'] : [];
  const sections: SectionSemantics[] = [];
  for (const entry of rows) {
    if (entry === null || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;
    const role = SEMANTIC_ROLES.includes(item['semanticRole'] as SemanticRole)
      ? (item['semanticRole'] as SemanticRole)
      : 'UNKNOWN';
    const profile = item['transientProfile'];
    sections.push({
      index: number(item['index']),
      intensityLabel: typeof item['intensityLabel'] === 'string' ? item['intensityLabel'] : '',
      semanticRole: role,
      semanticRoleConfidence: number(item['semanticRoleConfidence']),
      recurrenceGroup: number(item['recurrenceGroup']),
      recurrenceStrength: number(item['recurrenceStrength']),
      similarToSections: Array.isArray(item['similarToSections'])
        ? item['similarToSections'].filter((value): value is number => typeof value === 'number')
        : [],
      melodicActivity: number(item['melodicActivity']),
      harmonicActivity: number(item['harmonicActivity']),
      harmonicStability: number(item['harmonicStability']),
      transientProfile:
        profile !== null && typeof profile === 'object'
          ? {
              count: number((profile as Record<string, unknown>)['count']),
              dominant:
                typeof (profile as Record<string, unknown>)['dominant'] === 'string'
                  ? ((profile as Record<string, unknown>)['dominant'] as string)
                  : 'OTHER_TRANSIENT',
              confidence: number((profile as Record<string, unknown>)['confidence']),
              shares: readShares((profile as Record<string, unknown>)['shares']),
            }
          : null,
      evidence: Array.isArray(item['evidence'])
        ? item['evidence'].filter((value): value is string => typeof value === 'string')
        : [],
    });
  }
  return {
    version: typeof source['version'] === 'string' ? source['version'] : '',
    sections,
    phraseBoundaries: Array.isArray(source['phraseBoundaries'])
      ? source['phraseBoundaries'].filter((value): value is number => typeof value === 'number')
      : [],
  };
}

function number(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function readShares(raw: unknown): Record<string, number> {
  if (raw === null || typeof raw !== 'object') return {};
  const out: Record<string, number> = {};
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'number' && Number.isFinite(value)) out[name] = value;
  }
  return out;
}

function readVersions(raw: unknown): Record<string, string> {
  if (raw === null || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string') out[name] = value;
  }
  return out;
}

function readDirections(raw: unknown): Record<string, Record<string, unknown>> {
  if (raw === null || typeof raw !== 'object') return {};
  const out: Record<string, Record<string, unknown>> = {};
  for (const [difficulty, document] of Object.entries(raw as Record<string, unknown>)) {
    if (document === null || typeof document !== 'object' || Array.isArray(document)) continue;
    out[difficulty] = document as Record<string, unknown>;
  }
  return out;
}

function readRecord(source: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = source[key];
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ChartGenerationError('malformed-response', `Expected an object at "${key}".`);
  }
  return value as Record<string, unknown>;
}

function readString(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  if (typeof value !== 'string') {
    throw new ChartGenerationError('malformed-response', `Expected a string at "${key}".`);
  }
  return value;
}

function readNumber(source: Record<string, unknown>, key: string): number {
  const value = source[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new ChartGenerationError('malformed-response', `Expected a number at "${key}".`);
  }
  return value;
}

function readBoolean(source: Record<string, unknown>, key: string): boolean {
  return source[key] === true;
}

function readStringArray(source: Record<string, unknown>, key: string): string[] {
  const value = source[key];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
}
